<template>
  <div>
    <DxPopup
      :visible="visible"
      :title="$t('tools.erp_integration.consum_erp_summary_title')"
      :show-close-button="!sendingToErp"
      :width="popupWidth"
      :height="popupHeight"
      :full-screen="isMobileOrTablet"
      @hidden="onHidden"
      @hiding="onHiding"
    >
      <template #content>
        <div class="consum-summary-dialog-content">
          <DxTabPanel
            class="erp-summary-tabs"
            :height="'100%'"
            :animation-enabled="true"
            :swipe-enabled="false"
            :defer-rendering="false"
            :selected-index.sync="selectedTabIndex"
          >
            <DxTabItem :title="itemsTabTitle">
              <template #default>
                <div class="export-items-section">
                  <div v-if="validatingMappings" class="alert alert-info">{{ $t("common.please_wait") }}</div>
                  <div v-if="mappingValidationError" class="alert alert-danger">{{ mappingValidationError }}</div>
                  <div v-if="missingMappingsCount > 0" class="mapping-alert-banner">
                    <span>{{ $t("tools.erp_integration.consum_missing_mappings_banner", { count: missingMappingsCount }) }}</span>
                    <button type="button" class="btn btn-sm btn-outline-warning" @click="openMissingMappingsFeedback">
                      {{ $t("tools.erp_integration.consum_view_missing_mappings") }}
                    </button>
                  </div>
                  <p class="text-muted">{{ $t("tools.erp_integration.consum_erp_summary_items_hint") }}</p>
                  <p v-if="retryContext" class="alert alert-info">{{ $t("tools.erp_integration.consum_batch_retry_parameters_hint") }}</p>
                  <DxDataGrid
                    class="smagria-grid export-items-grid"
                    :data-source="exportItems"
                    key-expr="key"
                    :height="'100%'"
                    :show-borders="true"
                    :column-auto-width="true"
                    :column-min-width="110"
                    :row-alternation-enabled="true"
                    :no-data-text="$t('common.no_data_text')"
                  >
                    <DxPaging :enabled="false" />
                    <DxScrolling mode="virtual" />
                    <DxFilterRow :visible="true" />
                    <DxColumn data-field="method" :caption="$t('tools.erp_integration.consum_erp_summary_method')" cell-template="methodTemplate" :min-width="260" />
                    <DxColumn data-field="typeLabel" :caption="$t('tools.erp_integration.consum_erp_summary_item_type')" />
                    <DxColumn data-field="name" :caption="$t('tools.erp_integration.consum_erp_summary_item_name')" />
                    <DxColumn data-field="work_name" :caption="$t('works.work_approval.work_name')" />
                    <DxColumn data-field="parcel_name" :caption="$t('works.work_approval.parcel_name')" />
                    <DxColumn data-field="warehouse_name" :caption="$t('common.warehouse')" />
                    <DxColumn data-field="ownerUnitName" :caption="$t('tools.erp_integration.consum_erp_summary_buyer')" />
                    <DxColumn data-field="sellerName" :caption="$t('tools.erp_integration.consum_erp_summary_issuer')" cell-template="sellerTemplate" :min-width="200" />
                    <DxColumn data-field="originalDate" :caption="$t('tools.erp_integration.consum_erp_summary_original_date')" data-type="date" format="yyyy-MM-dd" />
                    <DxColumn data-field="invoiceDate" :caption="$t('tools.erp_integration.consum_erp_summary_invoice_date')" cell-template="itemDateTemplate" :min-width="170" />
                    <DxColumn data-field="quantity" :caption="$t('tools.erp_integration.consum_erp_summary_quantity')" data-type="number" format="#,##0.####" />
                    <DxColumn data-field="unit" :caption="$t('common.measure_unit')" />
                    <DxColumn data-field="price" :caption="$t('tools.erp_integration.consum_erp_summary_price')" data-type="number" format="#,##0.####" cell-template="priceTemplate" />
                    <DxColumn data-field="vatRateLabel" :caption="$t('tools.erp_integration.consum_erp_summary_vat_rate')" />
                    <DxColumn data-field="reverseCharge" :caption="$t('tools.erp_integration.consum_erp_summary_reverse_charge')" data-type="boolean" cell-template="reverseChargeTemplate" />
                    <template #methodTemplate="{ data }">
                      <DxSelectBox
                        v-if="data.data.component === 'CONSUM'"
                        :data-source="consumMethods"
                        value-expr="value"
                        display-expr="label"
                        :value="data.data.method"
                        :disabled="sendingToErp || itemLocked(data.data.key, 'method')"
                        :input-attr="{ 'aria-label': $t('tools.erp_integration.consum_erp_summary_method') }"
                        @value-changed="setItemMethod(data.data.key, $event.value)"
                      />
                      <span v-else>{{ $t('tools.erp_integration.consum_method_service') }}</span>
                    </template>
                    <template #reverseChargeTemplate="{ data }">
                      <DxCheckBox
                        :value="data.data.reverseCharge"
                        :disabled="sendingToErp || !data.data.billable || itemLocked(data.data.key)"
                        :hint="$t('tools.erp_integration.consum_erp_summary_reverse_charge_hint')"
                        :element-attr="{ 'aria-label': $t('tools.erp_integration.consum_erp_summary_reverse_charge') }"
                        @value-changed="setItemReverseCharge(data.data.key, $event.value)"
                      />
                    </template>
                    <template #priceTemplate="{ data }">
                      <span>{{ formatInvoicePrice(data.data.price) }}</span>
                    </template>
                    <template #sellerTemplate="{ data }">
                      <DxSelectBox
                        v-if="data.data.sellerEditable"
                        :data-source="invoiceSellers"
                        value-expr="uuid"
                        display-expr="name"
                        :value="data.data.sellerUuid"
                        :search-enabled="true"
                        :disabled="sendingToErp || validatingMappings || itemLocked(data.data.key)"
                        :input-attr="{ 'aria-label': $t('tools.erp_integration.consum_erp_summary_issuer') }"
                        @value-changed="setItemSeller(data.data.key, $event.value)"
                      />
                      <span v-else>{{ data.data.sellerName }}</span>
                    </template>
                    <template #itemDateTemplate="{ data }">
                      <DxDateBox
                        :value="data.data.invoiceDate"
                        type="date"
                        display-format="yyyy-MM-dd"
                        date-serialization-format="yyyy-MM-dd"
                        :disabled="sendingToErp || !data.data.billable || itemLocked(data.data.key)"
                        :is-valid="isValidInvoiceDate(data.data.invoiceDate)"
                        :input-attr="{ 'aria-label': $t('tools.erp_integration.consum_erp_summary_invoice_date') }"
                        @value-changed="setItemInvoiceDate(data.data.key, $event.value)"
                      />
                    </template>
                  </DxDataGrid>
                </div>
              </template>
            </DxTabItem>
            <DxTabItem :title="$t('tools.erp_integration.consum_erp_summary_transfers')">
              <template #default>
                <div class="preview-section">
                  <div v-if="mappingValidationError" class="alert alert-danger">{{ mappingValidationError }}</div>
                  <div v-if="validatingMappings || mappingValidationTimer" class="alert alert-info">{{ $t("common.please_wait") }}</div>
                  <DxDataGrid
                    class="smagria-grid"
                    :data-source="transferOperations"
                    :height="'100%'"
                    :show-borders="true"
                    :column-auto-width="true"
                    :no-data-text="$t('common.no_data_text')"
                  >
                    <DxPaging :enabled="false" />
                    <DxColumn data-field="operation" :caption="$t('tools.erp_integration.consum_erp_summary_item_type')" />
                    <DxColumn data-field="product_name" :caption="$t('tools.erp_integration.consum_erp_summary_item_name')" />
                    <DxColumn data-field="source" :caption="$t('tools.erp_integration.consum_erp_summary_transfer_source')" />
                    <DxColumn data-field="target" :caption="$t('tools.erp_integration.consum_erp_summary_transfer_target')" />
                    <DxColumn data-field="date" :caption="$t('tools.erp_integration.consum_erp_summary_original_date')" data-type="date" format="yyyy-MM-dd" />
                    <DxColumn data-field="quantity" :caption="$t('tools.erp_integration.consum_erp_summary_quantity')" data-type="number" format="#,##0.####" />
                  </DxDataGrid>
                </div>
              </template>
            </DxTabItem>
            <!-- 1. Fül: Consum -->
            <DxTabItem :title="consumTabTitle">
              <template #default>
                <div class="preview-section">
                  <!-- Figyelmeztető sáv, ha hiányzó leképezések vannak -->
                  <div v-if="missingMappingsCount > 0" class="mapping-alert-banner">
                    <div class="mapping-alert-content">
                      <i class="fa fa-exclamation-triangle me-2"></i>
                      <span>
                        {{ $t("tools.erp_integration.consum_missing_mappings_banner", { count: missingMappingsCount }) }}
                      </span>
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-warning mapping-alert-btn"
                      @click="openMissingMappingsFeedback"
                    >
                      <i class="fa fa-list-ul me-1"></i>
                      {{ $t("tools.erp_integration.consum_view_missing_mappings") }}
                    </button>
                  </div>

                  <div v-if="!consumSummaryByWarehouse.length" class="preview-ok">
                  {{ $t("common.no_data_text") }}
                </div>
                <div v-else class="preview-table-wrap">
                  <table class="preview-table">
                    <thead>
                      <tr>
                        <th>{{ $t("common.warehouse") }}</th>
                        <th>{{ $t("tools.erp_integration.consum_erp_summary_items_count") }}</th>
                        <th>{{ $t("tools.erp_integration.consum_erp_summary_products") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="group in consumSummaryByWarehouse" :key="'consum-' + group.warehouseName">
                        <td>{{ group.warehouseName }}</td>
                        <td>{{ group.itemsCount }}</td>
                        <td>
                          <div v-for="product in group.products" :key="product.key" class="product-item">
                            {{ product.productName }}: {{ formatQuantity(product.quantity) }} {{ product.measureUnitName }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </DxTabItem>

          <!-- 2. Fül: Kiadott számlák -->
          <DxTabItem :title="outgoingTabTitle">
            <template #default>
              <div class="preview-section">
                <div v-if="validatingMappings" class="alert alert-info">
                  {{ $t("common.please_wait") }}
                </div>
                <div v-else-if="mappingValidationError" class="alert alert-danger">
                  {{ mappingValidationError }}
                </div>
                <div v-if="loadingInvoiceSettings" class="alert alert-info">
                  {{ $t("common.please_wait") }}
                </div>
                <div v-else-if="invoiceSettingsError" class="alert alert-danger">
                  {{ invoiceSettingsError }}
                </div>
                <div
                  v-else-if="!invoiceServiceArticlesValid"
                  class="alert alert-warning"
                >
                  {{ $t("tools.erp_integration.consum_invoice_article_code_required") }}
                </div>
                <div v-if="!outgoingInvoiceCards.length" class="preview-ok">
                  {{ $t("common.no_data_text") }}
                </div>
                <div v-else class="outgoing-invoices-container">
                  <!-- Parcella tulajdonosonként elkülönített számlakártyák -->
                  <div
                    v-for="(card, index) in outgoingInvoiceCards"
                    :key="'out-invoice-' + card.key"
                    class="outgoing-invoice-card"
                  >
                    <div class="invoice-card-header">
                      <div class="invoice-title-wrapper">
                        <span class="invoice-badge">
                          {{ $t("tools.erp_integration.consum_erp_summary_outgoing_invoice_badge", { index: index + 1 }) }}
                        </span>
                        <h6 class="invoice-owner-name">{{ card.ownerUnitName }}</h6>
                      </div>
                      <div class="invoice-meta-badges">
                        <span class="meta-pill" :title="$t('tools.erp_integration.consum_erp_summary_vat_rate')">
                          {{ $t("tools.erp_integration.consum_erp_summary_vat_badge", { rate: card.vatRateLabel }) }}
                        </span>
                        <span
                          v-if="card.reverseCharge"
                          class="meta-pill meta-pill--reverse-charge"
                          :title="$t('tools.erp_integration.consum_erp_summary_reverse_charge')"
                        >
                          {{ $t("tools.erp_integration.consum_erp_summary_reverse_charge") }}
                        </span>
                        <span class="meta-pill" :title="$t('common.surface_ha')">
                          <i class="fa fa-vector-square"></i>
                          {{ formatQuantity(card.totalAreaHa) }} ha
                        </span>
                        <span class="meta-pill" :title="$t('tools.erp_integration.consum_erp_summary_value')">
                          <i class="fa fa-coins"></i>
                          {{ card.totalValue === null ? $t("tools.erp_integration.consum_erp_summary_price_tbd") : formatQuantity(card.totalValue) }}
                        </span>
                      </div>
                    </div>

                    <div class="invoice-card-meta">
                      <div class="meta-row">
                        <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_buyer") }}:</span>
                        <span class="meta-value">
                          {{ card.ownerUnitName }} —
                          {{ card.buyerPartnerCode || $t("tools.erp_integration.consum_erp_summary_partner_unavailable") }}
                        </span>
                      </div>
                      <div class="meta-row">
                        <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_supplier") }}:</span>
                        <span class="meta-value">{{ card.sourceUnitNames.join(", ") || "-" }}</span>
                      </div>
                      <div class="meta-row">
                        <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_month") }}:</span>
                        <span class="meta-value">{{ card.period }}</span>
                      </div>
                      <div class="meta-row">
                        <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_vat_rate") }}:</span>
                        <span class="meta-value">{{ card.vatRateLabel }}</span>
                      </div>
                      <div class="meta-row">
                        <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_reverse_charge") }}:</span>
                        <span class="meta-value">{{ card.reverseCharge ? $t("common.yes") : $t("common.no") }}</span>
                      </div>
                      <div class="meta-row">
                        <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_invoice_date") }}:</span>
                        <DxDateBox
                          :value="invoiceDates[card.key]"
                          type="date"
                          display-format="yyyy-MM-dd"
                          date-serialization-format="yyyy-MM-dd"
                          :disabled="sendingToErp || invoiceGroupLocked(card.key)"
                          :is-valid="isValidInvoiceDate(invoiceDates[card.key])"
                          :input-attr="{ 'aria-label': $t('tools.erp_integration.consum_erp_summary_invoice_date') }"
                          @value-changed="setInvoiceDate(card.key, $event.value)"
                        />
                      </div>
                    </div>

                    <div class="preview-table-wrap">
                      <table class="preview-table invoice-lines-table">
                        <thead>
                          <tr>
                            <th>{{ $t("tools.erp_integration.consum_erp_summary_cost_center") }}</th>
                            <th>{{ $t("tools.erp_integration.consum_erp_summary_invoice_line_note") }}</th>
                            <th>{{ $t("tools.erp_integration.consum_erp_summary_article_code") }}</th>
                            <th>{{ $t("tools.erp_integration.consum_erp_summary_quantity") }}</th>
                            <th>{{ $t("common.measure_unit") }}</th>
                            <th>{{ $t("tools.erp_integration.consum_erp_summary_value") }}</th>
                            <th>{{ $t("tools.erp_integration.consum_erp_summary_price") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="line in card.lines" :key="'out-line-' + line.key">
                            <td>
                              <span class="cost-center-tag" :class="'cost-center-tag--' + line.costCenter.toLowerCase()">
                                {{ line.costCenter }}
                              </span>
                            </td>
                            <td>{{ line.description }}</td>
                            <td><code>{{ line.articleCode || "-" }}</code></td>
                            <td>{{ line.quantity }}</td>
                            <td>{{ line.measureUnit }}</td>
                            <td><strong>{{ line.valueLabel }}</strong></td>
                            <td>{{ line.priceLabel }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </DxTabItem>

          <DxTabItem :title="incomingTabTitle">
            <template #default>
              <div class="preview-section">
                <div v-if="!incomingInvoiceCards.length" class="preview-ok">
                  {{ $t("common.no_data_text") }}
                </div>
                <div v-for="(card, index) in incomingInvoiceCards" :key="'incoming-' + card.key" class="outgoing-invoice-card">
                  <div class="invoice-card-header">
                    <div class="invoice-title-wrapper">
                      <span class="invoice-badge">
                        {{ $t("tools.erp_integration.consum_erp_summary_incoming_invoice_badge", { index: index + 1 }) }}
                      </span>
                      <h6 class="invoice-owner-name">{{ card.ownerUnitName }}</h6>
                    </div>
                    <div class="invoice-meta-badges">
                      <span class="meta-pill" :title="$t('tools.erp_integration.consum_erp_summary_vat_rate')">
                        {{ $t("tools.erp_integration.consum_erp_summary_vat_badge", { rate: card.vatRateLabel }) }}
                      </span>
                      <span
                        v-if="card.reverseCharge"
                        class="meta-pill meta-pill--reverse-charge"
                        :title="$t('tools.erp_integration.consum_erp_summary_reverse_charge')"
                      >
                        {{ $t("tools.erp_integration.consum_erp_summary_reverse_charge") }}
                      </span>
                    </div>
                  </div>
                  <div class="invoice-card-meta">
                    <div class="meta-row">
                      <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_supplier") }}:</span>
                      <span class="meta-value">{{ card.sourceUnitName }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_month") }}:</span>
                      <span class="meta-value">{{ card.period }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_vat_rate") }}:</span>
                      <span class="meta-value">{{ card.vatRateLabel }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_reverse_charge") }}:</span>
                      <span class="meta-value">{{ card.reverseCharge ? $t("common.yes") : $t("common.no") }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">{{ $t("tools.erp_integration.consum_erp_summary_invoice_date") }}:</span>
                      <DxDateBox
                        :value="invoiceDates[card.key]"
                        type="date"
                        display-format="yyyy-MM-dd"
                        date-serialization-format="yyyy-MM-dd"
                        :disabled="sendingToErp || invoiceGroupLocked(card.key)"
                        :is-valid="isValidInvoiceDate(invoiceDates[card.key])"
                        :input-attr="{ 'aria-label': $t('tools.erp_integration.consum_erp_summary_invoice_date') }"
                        @value-changed="setInvoiceDate(card.key, $event.value)"
                      />
                    </div>
                  </div>
                  <div class="preview-table-wrap">
                    <table class="preview-table invoice-lines-table">
                      <thead>
                        <tr>
                          <th>{{ $t("tools.erp_integration.consum_erp_summary_cost_center") }}</th>
                          <th>{{ $t("tools.erp_integration.consum_erp_summary_products") }}</th>
                          <th>{{ $t("tools.erp_integration.consum_erp_summary_quantity") }}</th>
                          <th>{{ $t("common.measure_unit") }}</th>
                          <th>{{ $t("tools.erp_integration.consum_erp_summary_price") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="line in card.lines" :key="line.key">
                          <td>
                            <span v-if="line.costCenter" class="cost-center-tag cost-center-tag--incoming">
                              {{ line.costCenter }}
                            </span>
                            <span v-else>-</span>
                          </td>
                          <td>{{ line.name }}</td>
                          <td>{{ formatQuantity(line.quantity) }}</td>
                          <td>{{ line.unit }}</td>
                          <td>{{ line.price === null || line.price === undefined ? $t("tools.erp_integration.consum_erp_summary_price_tbd") : formatQuantity(line.price) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </template>
          </DxTabItem>

        </DxTabPanel>

          <div v-if="!invoiceDatesValid" class="alert alert-danger">
            {{ $t("tools.erp_integration.consum_erp_summary_invoice_date_required") }}
          </div>
          <div v-if="!invoiceSellersValid" class="alert alert-danger">
            {{ $t("tools.erp_integration.consum_erp_summary_issuer_required") }}
          </div>
          <div class="settings-actions">
            <button type="button" class="btn btn-secondary btn-sm me-2" :disabled="sendingToErp" @click="close">
              {{ $t("common.close") }}
            </button>
            <button
              type="button"
              class="erp-send-button"
              :disabled="sendingToErp || !invoiceDatesValid || !invoiceSellersValid || validatingMappings || !!mappingValidationTimer || !!mappingValidationError || missingMappingsCount > 0 || loadingInvoiceSettings || !!invoiceSettingsError || !invoiceServiceArticlesValid || !selectedRows.length"
              @click="onSendToErp"
            >
              <i v-if="sendingToErp" class="fa fa-spinner fa-spin me-1"></i>
              {{ $t("tools.erp_integration.consum_send_to_erp") }}
            </button>
          </div>
        </div>
      </template>
    </DxPopup>

    <ConsumExportFeedbackDialog
      :visible.sync="feedbackVisible"
      :mode="feedbackMode"
      :document="feedbackDocument"
      :errors="feedbackErrors"
      :issues="feedbackIssues"
      :invoice-exports="feedbackInvoiceExports"
      :selected-rows="selectedRows"
      :message="feedbackMessage"
      :queue-uuid="feedbackQueueUuid"
      @close="onFeedbackClosed"
      @unblocked="onConsumUnblocked"
    />
  </div>
</template>

<script>
import DxPopup from "devextreme-vue/popup";
import DxDateBox from "devextreme-vue/date-box";
import DxSelectBox from "devextreme-vue/select-box";
import DxCheckBox from "devextreme-vue/check-box";
import DxDataGrid, { DxColumn, DxPaging, DxScrolling, DxFilterRow } from "devextreme-vue/data-grid";
import DxTabPanel, { DxItem as DxTabItem } from "devextreme-vue/tab-panel";
import { mapActions, mapGetters } from "vuex";
import ConsumExportFeedbackDialog from "./ConsumExportFeedbackDialog.vue";

export default {
  name: "ConsumSummaryExportDialog",
  components: {
    DxPopup,
    DxDateBox,
    DxSelectBox,
    DxCheckBox,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxScrolling,
    DxFilterRow,
    DxTabPanel,
    DxTabItem,
    ConsumExportFeedbackDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    retryContext: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isMobileOrTablet: typeof window !== "undefined" ? window.innerWidth < 992 : false,
      sendingToErp: false,
      feedbackVisible: false,
      feedbackMode: "SUCCESS",
      feedbackDocument: {},
      feedbackErrors: [],
      feedbackIssues: [],
      feedbackInvoiceExports: [],
      feedbackMessage: "",
      feedbackQueueUuid: "",
      exportAttempted: false,
      missingMappingsCount: 0,
      cachedMissingMappings: [],
      invoiceCostCenters: {},
      invoicePrices: {},
      invoiceItemDates: {},
      invoiceItemSellers: {},
      invoiceItemReverseCharges: {},
      invoiceItemMethods: {},
      stockOperations: [],
      invoiceItemParties: {},
      invoiceServiceArticles: {},
      invoiceSellers: [],
      selectedTabIndex: 0,
      mappingValidationTimer: null,
      validatingMappings: false,
      mappingValidationRequestId: 0,
      mappingValidationError: "",
      loadingInvoiceSettings: false,
      invoiceSettingsError: "",
    };
  },
  computed: {
    ...mapGetters({
      settingGroups: "settings/settingGroups",
    }),
    popupWidth() {
      return this.isMobileOrTablet ? "100%" : "85%";
    },
    popupHeight() {
      return this.isMobileOrTablet ? "100%" : "80%";
    },
    itemsTabTitle() {
      return `${this.$t("tools.erp_integration.consum_erp_summary_items_section")} (${this.exportItems.length})`;
    },
    consumMethods() {
      return [
        { value: "VANZARE_DE_SERVICII", label: this.$t("tools.erp_integration.consum_method_service") },
        { value: "CONSUM", label: this.$t("tools.erp_integration.consum_method_consum") },
        { value: "VANZARE_DE_STOC_SI_CONSUM", label: this.$t("tools.erp_integration.consum_method_stock") },
      ];
    },
    transferOperations() {
      return this.stockOperations.filter((step) => step.operation !== "BONURI_CONSUM");
    },
    exportItems() {
      const items = new Map();
      this.selectedRows.forEach((row) => {
        if (row.has_consum !== false) {
          items.set(row.uuid, { key: row.uuid, component: "CONSUM", sourceRows: [row] });
        }
        const key = this.workItemKey(row);
        if (!items.has(key)) {
          items.set(key, { key, component: "WORK", sourceRows: [] });
        }
        items.get(key).sourceRows.push(row);
      });
      return Array.from(items.values()).map((item) => {
        const row = item.sourceRows.find((source) => source.has_consum !== false) || item.sourceRows[0];
        const isWork = item.component === "WORK";
        const method = isWork ? "VANZARE_DE_SERVICII"
          : this.invoiceItemMethods[item.key] || this.defaultConsumMethod(row);
        const sellerEditable = isWork && item.sourceRows.every((source) => source.has_consum === false);
        const originalSeller = row.has_consum === false ? row.department_uuid
          : row.warehouse_department_uuid || row.source_department_uuid;
        const parties = this.invoiceItemParties[item.key];
        const sellerUuid = sellerEditable && Object.prototype.hasOwnProperty.call(this.invoiceItemSellers, item.key)
          ? this.invoiceItemSellers[item.key]
          : !sellerEditable && parties?.seller_department_uuid ? parties.seller_department_uuid : originalSeller;
        const sellerOption = this.invoiceSellers.find((seller) => seller.uuid === sellerUuid);
        const originalDate = String(row.consum_date || row.work_date || "").slice(0, 10);
        const prices = item.sourceRows.map((source) => this.normalizeInvoicePrice(
          this.invoicePrices[source.uuid]?.[isWork ? "work_unit_price" : "product_unit_price"]
        ));
        const price = prices.every((value) => value !== null && value === prices[0]) ? prices[0] : null;
        const currentParties = parties?.seller_department_uuid === sellerUuid ? parties : null;
        const sourcePair = row.erp_invoice_group_key;
        const sellerKey = currentParties?.seller_key || sellerOption?.party_key
          || (sellerUuid === originalSeller ? row.erp_invoice_seller_key || sourcePair : null) || sellerUuid;
        const buyerKey = currentParties?.buyer_key || row.erp_invoice_buyer_key
          || (sourcePair ? "mapped-buyer" : row.parcel_department_uuid);
        const vatRate = this.normalizeVatRate(
          parties && Object.prototype.hasOwnProperty.call(parties, "vat_rate")
            ? parties.vat_rate
            : row[isWork ? "erp_invoice_work_vat_rate" : "erp_invoice_product_vat_rate"] ?? row.vat_rate
        );
        return {
          ...item,
          method,
          billable: isWork || method === "VANZARE_DE_SERVICII",
          typeLabel: this.$t(`tools.erp_integration.${isWork
            ? sellerEditable ? "consum_erp_summary_standalone_work" : "consum_erp_summary_linked_work"
            : "consum_erp_summary_consum_section"}`),
          name: isWork ? row.work_name || "-" : row.product_name || "-",
          work_name: row.work_name || "-",
          parcel_name: row.parcel_name || "-",
          warehouse_name: isWork ? "-" : row.warehouse_name || "-",
          ownerUnitName: row.parcel_legal_entity_name || row.parcel_department_name || "-",
          buyerPartnerCode: row.parcel_erp_partner_code || null,
          sellerUuid,
          sellerName: sellerOption?.name || row.warehouse_department_name || row.source_department_name || row.department_name || "-",
          sellerEditable,
          sellerKey,
          buyerKey,
          originalDate,
          invoiceDate: Object.prototype.hasOwnProperty.call(this.invoiceItemDates, item.key)
            ? this.invoiceItemDates[item.key] : originalDate,
          quantity: Number(isWork ? row.parcel_area_ha : row.quantity) || 0,
          unit: isWork ? "HA" : row.measure_unit_name || "-",
          price,
          priceLabel: this.formatInvoicePrice(price),
          vatRate,
          vatRateLabel: this.formatVatRate(vatRate),
          reverseCharge: this.invoiceItemReverseCharges[item.key] === true,
          costCenter: this.invoiceCostCenters[row.uuid] || null,
        };
      });
    },
    invoiceDates() {
      return Object.fromEntries(this.invoiceGroups.map((group) => [group.key, group.invoiceDate]));
    },
    invoiceSellersValid() {
      return this.exportItems.every((item) => !item.sellerEditable || !!item.sellerUuid);
    },
    consumSummaryByWarehouse() {
      const rows = this.selectedRows.filter((row) => row.has_consum !== false).map((row) => {
        const method = this.invoiceItemMethods[row.uuid] || this.defaultConsumMethod(row);
        const operation = this.stockOperations.find((step) => step.consum_uuid === row.uuid && step.operation === "BONURI_CONSUM");
        return method === "VANZARE_DE_SERVICII" ? row : { ...row, warehouse_name: operation?.target || "-" };
      });
      return this.groupConsumByWarehouse(rows);
    },
    consumTabTitle() {
      const count = this.consumSummaryByWarehouse.length;
      return `${this.$t("tools.erp_integration.consum_erp_summary_consum_section")} (${count})`;
    },
    outgoingTabTitle() {
      const count = this.outgoingInvoiceCards.length;
      return `${this.$t("tools.erp_integration.consum_erp_summary_outgoing_section")} (${count})`;
    },
    incomingTabTitle() {
      return `${this.$t("tools.erp_integration.consum_erp_summary_incoming_section")} (${this.incomingInvoiceCards.length})`;
    },
    invoiceServiceArticleCode() {
      if (this.invoiceGroups.length > 0 && this.invoiceGroups.every((group) => !!group.serviceArticleCode)) {
        return this.invoiceGroups[0].serviceArticleCode;
      }
      if (this.invoiceGroups.length > 0 && this.invoiceGroups.some((group) => group.vatRate !== null)) {
        return "";
      }
      const group = (this.settingGroups || []).find((settingGroup) => settingGroup?.code === "ERP_INTEGRATION");
      const setting = (group?.items || []).find((item) => item?.code === "ERP_CONSUM_INVOICE_SERVICE_ARTICLE_CODE");
      return String(setting?.value || "").trim();
    },
    invoiceServiceArticlesValid() {
      if (!this.retryContext) return !this.invoiceGroups.length || !!this.invoiceServiceArticleCode;
      return this.invoiceGroups.every((group) => this.invoiceGroupLocked(group.key) || !!group.serviceArticleCode
        || (group.vatRate === null && !!this.invoiceServiceArticleCode));
    },
    // Each card represents one invoice for an ERP seller and buyer.
    outgoingInvoiceCards() {
      return this.invoiceGroups.map((group) => {
        const workUnitPrice = group.workValue !== null && group.totalAreaHa > 0
          ? group.workValue / group.totalAreaHa : null;
        const articleCode = group.serviceArticleCode
          || (group.vatRate === null ? this.invoiceServiceArticleCode : "")
          || this.$t("tools.erp_integration.consum_invoice_article_code_not_configured");
        const description = this.invoiceServiceDescriptionForVat(group.vatRate);
        return {
          key: group.key,
          ownerUnitName: group.ownerUnitName,
          buyerPartnerCode: group.buyerPartnerCode,
          sourceUnitNames: group.sourceUnitNames,
          period: group.period,
          vatRate: group.vatRate,
          vatRateLabel: group.vatRateLabel,
          reverseCharge: group.reverseCharge,
          serviceArticleCode: group.serviceArticleCode,
          totalAreaHa: group.totalAreaHa,
          totalValue: group.totalValue,
          lines: [
            {
              key: `input-${group.key}`,
              costCenter: "InputServ",
              description,
              articleCode,
              quantity: 1,
              measureUnit: "Lei",
              valueLabel: this.formatInvoicePrice(group.inputValue),
              priceLabel: this.formatInvoicePrice(group.inputValue),
            },
            {
              key: `work-${group.key}`,
              costCenter: "LandServ",
              description,
              articleCode,
              quantity: this.formatQuantity(group.totalAreaHa),
              measureUnit: "Lei",
              valueLabel: this.formatInvoicePrice(group.workValue),
              priceLabel: this.formatInvoicePrice(workUnitPrice),
            },
          ].filter((line) => line.costCenter === "InputServ" ? group.hasProducts : group.hasWorks),
        };
      });
    },
    invoiceGroups() {
      return this.groupInvoiceByBuyerAndSeller(this.exportItems.filter((item) => item.billable));
    },
    invoiceDatesValid() {
      return this.exportItems.every((item) => this.isValidInvoiceDate(item.invoiceDate));
    },
    incomingInvoiceCards() {
      return this.invoiceGroups.map((group) => {
        const lines = this.exportItems.filter((item) => item.billable && this.invoiceGroupKey(item) === group.key)
          .map(({ key, name, quantity, unit, price, costCenter, vatRate, vatRateLabel, reverseCharge }) => ({
            key, name, quantity, unit, price, costCenter, vatRate, vatRateLabel, reverseCharge,
          }));
        return { ...group, lines };
      });
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.selectedTabIndex = 0;
        this.invoiceItemSellers = {};
        this.invoiceItemParties = {};
        this.invoiceServiceArticles = {};
        this.initializeInvoiceDates();
        this.loadInvoiceSettings();
        this.checkMappings();
      } else {
        clearTimeout(this.mappingValidationTimer);
        this.mappingValidationTimer = null;
        ++this.mappingValidationRequestId;
        this.validatingMappings = false;
      }
    },
    selectedRows() {
      if (this.visible) {
        this.initializeInvoiceDates(true);
        this.checkMappings();
      }
    },
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
    if (this.visible) {
      this.initializeInvoiceDates();
      this.loadInvoiceSettings();
      this.checkMappings();
    }
  },
  beforeDestroy() {
    clearTimeout(this.mappingValidationTimer);
    ++this.mappingValidationRequestId;
    window.removeEventListener("resize", this.checkScreenSize);
  },
  methods: {
    ...mapActions({
      getSettingGroups: "settings/getSettingGroups",
    }),
    checkScreenSize() {
      this.isMobileOrTablet = typeof window !== "undefined" && window.innerWidth < 992;
    },
    initializeInvoiceDates(preserve = false) {
      if (this.retryContext && !preserve) {
        const settings = this.retryContext.invoice_items;
        this.invoiceItemDates = Object.fromEntries(Object.entries(settings).map(([key, config]) => [key, config.invoice_date]));
        this.invoiceItemMethods = Object.fromEntries(Object.entries(settings).map(([key, config]) => [key, config.method || "VANZARE_DE_SERVICII"]));
        this.invoiceItemReverseCharges = Object.fromEntries(Object.entries(settings).map(([key, config]) => [key, config.reverse_charge === true]));
        this.invoiceItemSellers = Object.fromEntries(Object.entries(settings).filter(([, config]) => config.seller_department_uuid)
          .map(([key, config]) => [key, config.seller_department_uuid]));
        this.invoiceItemParties = {};
        this.stockOperations = this.retryContext.stock_operations || [];
        return;
      }
      const dates = {};
      this.exportItems.forEach((item) => {
        dates[item.key] = preserve && Object.prototype.hasOwnProperty.call(this.invoiceItemDates, item.key)
          ? this.invoiceItemDates[item.key] : item.originalDate;
      });
      this.invoiceItemDates = dates;
      this.invoiceItemMethods = Object.fromEntries(this.exportItems.filter((item) => item.component === "CONSUM")
        .map((item) => [item.key, preserve ? item.method : this.defaultConsumMethod(item.sourceRows[0])]));
      this.stockOperations = [];
      this.invoiceItemSellers = preserve ? Object.fromEntries(Object.entries(this.invoiceItemSellers)
        .filter(([key]) => Object.prototype.hasOwnProperty.call(dates, key))) : {};
      this.invoiceItemParties = preserve ? Object.fromEntries(Object.entries(this.invoiceItemParties)
        .filter(([key]) => Object.prototype.hasOwnProperty.call(dates, key))) : {};
      this.invoiceItemReverseCharges = preserve ? Object.fromEntries(Object.entries(this.invoiceItemReverseCharges)
        .filter(([key]) => Object.prototype.hasOwnProperty.call(dates, key))) : {};
    },
    setInvoiceDate(key, value) {
      if (this.invoiceGroupLocked(key)) return;
      const items = this.exportItems.filter((item) => item.billable && this.invoiceGroupKey(item) === key);
      if (!items.length || items.every((item) => item.invoiceDate === value)) return;
      items.forEach((item) => this.$set(this.invoiceItemDates, item.key, value));
      this.scheduleMappingCheck();
    },
    setItemInvoiceDate(key, value) {
      if (this.itemLocked(key)) return;
      if (this.invoiceItemDates[key] === value) return;
      this.$set(this.invoiceItemDates, key, value);
      this.scheduleMappingCheck();
    },
    setItemSeller(key, value) {
      const item = this.exportItems.find((item) => item.key === key);
      if (!item?.sellerEditable || this.itemLocked(key) || item.sellerUuid === value) return;
      this.$set(this.invoiceItemSellers, key, value);
      this.scheduleMappingCheck();
    },
    setItemReverseCharge(key, value) {
      if (this.itemLocked(key)) return;
      const normalized = value === true;
      if ((this.invoiceItemReverseCharges[key] === true) === normalized) return;
      this.$set(this.invoiceItemReverseCharges, key, normalized);
      this.scheduleMappingCheck();
    },
    defaultConsumMethod(row) {
      const name = String(row.product_name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      return /\b(motorina|ad\s*blue)\b/i.test(name) ? "CONSUM" : "VANZARE_DE_SERVICII";
    },
    setItemMethod(key, method) {
      const item = this.exportItems.find((entry) => entry.key === key);
      if (!item || this.itemLocked(key, "method") || item.component !== "CONSUM" || item.method === method
        || !this.consumMethods.some((option) => option.value === method)) return;
      this.$set(this.invoiceItemMethods, key, method);
      this.stockOperations = [];
      this.scheduleMappingCheck();
    },
    itemLocked(key, field = "invoice") {
      return !!this.retryContext && (this.retryContext.read_only === true
        || this.retryContext.locks?.[key]?.[field] !== false);
    },
    invoiceGroupLocked(key) {
      return this.exportItems.some((item) => item.billable && this.invoiceGroupKey(item) === key && this.itemLocked(item.key));
    },
    scheduleMappingCheck() {
      clearTimeout(this.mappingValidationTimer);
      ++this.mappingValidationRequestId;
      this.validatingMappings = false;
      this.mappingValidationTimer = null;
      if (!this.visible || !this.invoiceDatesValid || !this.invoiceSellersValid) return;
      this.mappingValidationTimer = setTimeout(() => {
        this.mappingValidationTimer = null;
        this.checkMappings();
      }, 200);
    },
    invoiceItemSettings() {
      return Object.fromEntries(this.exportItems.map((item) => [
        item.key,
        {
          invoice_date: item.invoiceDate,
          reverse_charge: item.reverseCharge,
          method: item.method,
          ...(item.sellerEditable && item.sellerUuid ? { seller_department_uuid: item.sellerUuid } : {}),
        },
      ]));
    },
    isValidInvoiceDate(value) {
      if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return false;
      }
      const date = new Date(`${value}T00:00:00Z`);
      return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
    },
    close() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    async loadInvoiceSettings() {
      this.loadingInvoiceSettings = true;
      this.invoiceSettingsError = "";
      try {
        await this.getSettingGroups();
      } catch (error) {
        this.invoiceSettingsError = this.$t("tools.erp_integration.consum_invoice_settings_load_failed");
        console.error("Failed to load ERP invoice settings", error);
      } finally {
        this.loadingInvoiceSettings = false;
      }
    },
    onHidden() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    onHiding(event) {
      if (this.sendingToErp) event.cancel = true;
    },
    async onSendToErp() {
      if (this.sendingToErp) {
        return;
      }
      if (!this.invoiceDatesValid) {
        this.feedbackMode = "ERP_ERROR";
        this.feedbackErrors = [this.$t("tools.erp_integration.consum_erp_summary_invoice_date_required")];
        this.feedbackVisible = true;
        return;
      }
      if (!this.invoiceSellersValid) {
        this.feedbackMode = "ERP_ERROR";
        this.feedbackErrors = [this.$t("tools.erp_integration.consum_erp_summary_issuer_required")];
        this.feedbackVisible = true;
        return;
      }

      const selection = this.exportSelection();
      if (!selection.consum_uuids.length && !selection.work_sheet_uuids?.length) {
        return;
      }

      this.sendingToErp = true;
      try {
        let response = await this.$store.dispatch(this.retryContext
          ? "erpIntegration/retryConsumQueueItem" : "erpIntegration/exportConsumToErp", this.retryContext ? {
          uuid: this.retryContext.uuid,
          mode: "IMMEDIATE",
          retry_revision: this.retryContext.revision,
          invoice_items: this.invoiceItemSettings(),
        } : {
          ...selection,
          invoice_items: this.invoiceItemSettings(),
        });
        if (this.retryContext) {
          if (response?.uuid !== this.retryContext.uuid || typeof response.status !== "string") {
            throw new Error(this.$t("tools.erp_integration.consum_batch_retry_invalid_response"));
          }
          response = {
            success: response.status === "CONFIRMED",
            mode: response.status === "PARTIAL_SUCCESS" ? "PARTIAL_SUCCESS" : response.status === "CONFIRMED" ? "SUCCESS" : "ERP_ERROR",
            queue_uuid: response.uuid,
            invoice_exports: response.invoices || [],
            document: {
              numar: response.wm_document_number,
              serie: response.wm_document_series,
              cod_ies: response.wm_cod_ies,
              count: response.mapping_snapshot_json?.stock_steps?.filter((step) =>
                step.operation === "BONURI_CONSUM" && step.status === "CONFIRMED").length
                || response.wm_response_json?.DocumenteImportate?.length
                || response.invoices?.filter((invoice) => invoice.status === "CONFIRMED").length,
            },
            message: response.status === "CONFIRMED" ? "" : response.last_error_message
              || this.$t("tools.erp_integration.consum_batch_retry_incomplete"),
          };
        }

        this.feedbackInvoiceExports = response?.invoice_exports || [];
        this.feedbackQueueUuid = response?.queue_uuid || "";
        this.exportAttempted = !!response?.queue_uuid || !!response?.invoice_exports?.length;
        if (response?.mode === "PARTIAL_SUCCESS") {
          this.feedbackMode = "PARTIAL_SUCCESS";
          this.feedbackMessage = response.message || "";
          this.feedbackVisible = true;
        } else if (response?.success) {
          this.feedbackMode = "SUCCESS";
          this.feedbackDocument = response.document || {};
          this.feedbackMessage = response.message || "";
          this.feedbackVisible = true;
        } else if (response?.mode === "MISSING_MAPPINGS") {
          this.feedbackMode = "MISSING_MAPPINGS";
          this.feedbackIssues = response.issues || response.missing_mappings || [];
          this.feedbackMessage = response.message || "";
          this.feedbackVisible = true;
        } else {
          this.feedbackMode = "ERP_ERROR";
          this.feedbackErrors = this.normalizeErrors(response?.errors, response?.message);
          this.feedbackMessage = response?.message || "";
          this.feedbackVisible = true;
        }
      } catch (err) {
        const errData = err?.response?.data || {};
        this.feedbackInvoiceExports = errData.invoice_exports || [];
        this.exportAttempted = !!this.retryContext || (errData?.mode !== "ALREADY_ATTEMPTED"
          && (!!errData.queue_uuid || !!errData.invoice_exports?.length));
        this.feedbackQueueUuid = errData.queue_uuid || this.retryContext?.uuid || "";
        if (errData?.mode === "ALREADY_ATTEMPTED") {
          this.feedbackMode = "ALREADY_ATTEMPTED";
          this.feedbackMessage = errData.message || "";
        } else if (errData?.mode === "MISSING_MAPPINGS") {
          this.feedbackMode = "MISSING_MAPPINGS";
          this.feedbackIssues = errData.issues || errData.missing_mappings || [];
          this.feedbackMessage = errData.message || "";
        } else {
          this.feedbackMode = "ERP_ERROR";
          this.feedbackErrors = this.normalizeErrors(errData.errors, errData.message || err.message);
          this.feedbackMessage = errData.message || err.message;
        }
        this.feedbackVisible = true;
      } finally {
        this.sendingToErp = false;
      }
    },
    onConsumUnblocked() {
      this.exportAttempted = false;
      this.$emit("refresh");
    },
    normalizeErrors(errors, fallbackMessage = "") {
      const normalize = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) {
          return val.flatMap((item) => (typeof item === "object" ? normalize(item) : [String(item)]));
        }
        if (typeof val === "object") {
          const list = [];
          if (val.error) {
            list.push(...normalize(val.error));
          }
          Object.entries(val).forEach(([k, v]) => {
            if (k === "error" || k === "type") return;
            list.push(...normalize(v));
          });
          if (list.length) return list;
          return [JSON.stringify(val)];
        }
        return [String(val)];
      };

      const result = normalize(errors);
      if (result.length) {
        return result;
      }
      return fallbackMessage ? [String(fallbackMessage)] : [];
    },
    async checkMappings() {
      clearTimeout(this.mappingValidationTimer);
      this.mappingValidationTimer = null;
      const selection = this.exportSelection();
      const requestId = ++this.mappingValidationRequestId;
      this.mappingValidationError = "";
      if (!selection.consum_uuids.length && !selection.work_sheet_uuids?.length) {
        this.missingMappingsCount = 0;
        this.cachedMissingMappings = [];
        this.validatingMappings = false;
        return;
      }

      this.validatingMappings = true;
      try {
        const result = await this.$store.dispatch("erpIntegration/validateConsumExport", {
          ...selection,
          include_invoices: true,
          invoice_items: this.invoiceItemSettings(),
          ...(this.retryContext ? { retry_queue_uuid: this.retryContext.uuid, retry_revision: this.retryContext.revision } : {}),
        });
        if (requestId !== this.mappingValidationRequestId || !this.visible) {
          return;
        }
        if (!Array.isArray(result?.invoice_sellers) || !result?.invoice_item_parties
          || this.exportItems.some((item) => !result.invoice_item_parties[item.key])) {
          throw new Error("Invalid ERP invoice item preview response");
        }
        const issues = result?.missing_mappings || result?.issues || [];
        if (result?.valid === false && !issues.length) {
          throw new Error("ERP validation failed without an explanation");
        }
        this.missingMappingsCount = (!result?.valid && issues.length) ? issues.length : 0;
        this.cachedMissingMappings = issues;
        this.invoiceCostCenters = result?.invoice_cost_centers || {};
        this.invoicePrices = result?.invoice_prices || {};
        this.invoiceItemParties = result?.invoice_item_parties || {};
        this.invoiceServiceArticles = result?.invoice_service_articles || {};
        this.invoiceSellers = result?.invoice_sellers || [];
        this.stockOperations = result?.stock_operations || [];
      } catch (e) {
        if (requestId === this.mappingValidationRequestId && this.visible) {
          this.mappingValidationError = this.retryContext
            ? this.normalizeErrors(e?.response?.data?.errors,
              e?.response?.data?.message || this.$t("tools.erp_integration.consum_erp_summary_preview_load_failed")).join("; ")
            : this.$t("tools.erp_integration.consum_erp_summary_preview_load_failed");
          console.error("Failed to validate ERP consum invoice mappings", e);
        }
      } finally {
        if (requestId === this.mappingValidationRequestId) {
          this.validatingMappings = false;
        }
      }
    },
    openMissingMappingsFeedback() {
      this.feedbackMode = "MISSING_MAPPINGS";
      this.feedbackIssues = this.cachedMissingMappings || [];
      this.feedbackMessage = this.$t("tools.erp_integration.consum_feedback_missing_heading");
      this.feedbackVisible = true;
    },
    onFeedbackClosed() {
      const retryAttempted = !!this.retryContext && this.exportAttempted;
      if (this.exportAttempted) {
        this.$emit("exported", { clearSelection: this.feedbackMode === "SUCCESS" });
        this.exportAttempted = false;
      }
      if (this.feedbackMode === "SUCCESS" || retryAttempted) {
        this.close();
      }
    },
    groupConsumByWarehouse(rows) {
      const groups = new Map();

      rows.forEach((row) => {
        const warehouseName = row.warehouse_name || "-";
        if (!groups.has(warehouseName)) {
          groups.set(warehouseName, { warehouseName, itemsCount: 0, products: new Map() });
        }
        const group = groups.get(warehouseName);
        group.itemsCount += 1;

        const productKey = `${row.product_name || "-"}__${row.measure_unit_name || "-"}`;
        if (!group.products.has(productKey)) {
          group.products.set(productKey, {
            key: productKey,
            productName: row.product_name || "-",
            measureUnitName: row.measure_unit_name || "-",
            quantity: 0,
          });
        }
        group.products.get(productKey).quantity += Number(row.quantity) || 0;
      });

      return Array.from(groups.values()).map((group) => ({
        warehouseName: group.warehouseName,
        itemsCount: group.itemsCount,
        products: Array.from(group.products.values()),
      }));
    },
    groupInvoiceByBuyerAndSeller(items) {
      const groups = new Map();

      items.forEach((item) => {
        const key = this.invoiceGroupKey(item);
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            periods: new Set(),
            ownerUnitName: item.ownerUnitName,
            buyerPartnerCode: item.buyerPartnerCode,
            sourceUnitNames: new Set(),
            invoiceDate: item.invoiceDate,
            vatRate: item.vatRate,
            vatRateLabel: item.vatRateLabel,
            reverseCharge: item.reverseCharge,
            serviceArticleCode: this.invoiceServiceArticleCodeForVat(item.vatRate),
            inputValue: 0,
            hasProducts: false,
            hasWorks: false,
            inputPricesValid: true,
            workLines: [],
          });
        }
        const group = groups.get(key);
        group.periods.add(item.originalDate.slice(0, 7));
        group.sourceUnitNames.add(item.sellerName);
        if (item.component === "CONSUM") {
          group.hasProducts = true;
          if (item.price === null || item.quantity <= 0) {
            group.inputPricesValid = false;
          } else {
            group.inputValue += item.price * item.quantity;
          }
        } else {
          group.hasWorks = true;
          group.workLines.push(item);
        }
      });

      return Array.from(groups.values()).map((group) => {
        const workLines = group.workLines;
        const totalAreaHa = workLines.reduce((acc, line) => acc + line.quantity, 0);
        const workPricesValid = workLines.every((line) => line.price !== null && line.quantity > 0);
        const inputValue = group.inputPricesValid ? group.inputValue : null;
        const workValue = workPricesValid
          ? workLines.reduce((acc, line) => acc + line.quantity * line.price, 0)
          : null;

        return {
          key: group.key,
          hasProducts: group.hasProducts,
          hasWorks: group.hasWorks,
          workOnly: !group.hasProducts,
          invoiceDate: group.invoiceDate,
          vatRate: group.vatRate,
          vatRateLabel: group.vatRateLabel,
          reverseCharge: group.reverseCharge,
          serviceArticleCode: group.serviceArticleCode,
          period: Array.from(group.periods).filter(Boolean).sort().join(", "),
          ownerUnitName: group.ownerUnitName,
          buyerPartnerCode: group.buyerPartnerCode,
          sourceUnitNames: Array.from(group.sourceUnitNames),
          sourceUnitName: Array.from(group.sourceUnitNames).join(", "),
          totalAreaHa,
          inputValue,
          workValue,
          totalValue: inputValue !== null && workValue !== null ? inputValue + workValue : null,
        };
      });
    },
    invoiceGroupKey(item) {
      return JSON.stringify([item.sellerKey, item.buyerKey, item.invoiceDate, item.vatRate, item.reverseCharge]);
    },
    invoiceServiceArticleCodeForVat(vatRate = null) {
      const rate = this.normalizeVatRate(vatRate);
      if (rate !== null && this.invoiceServiceArticles && this.invoiceServiceArticles[String(rate)]) {
        return String(this.invoiceServiceArticles[String(rate)]).trim();
      }
      const group = (this.settingGroups || []).find((settingGroup) => settingGroup?.code === "ERP_INTEGRATION");
      const items = group?.items || [];
      if (rate === 0 || rate === 5 || rate === 11) {
        const rateSetting = items.find((item) => item?.code === `ERP_CONSUM_INVOICE_SERVICE_ARTICLE_CODE_${rate}`);
        return String(rateSetting?.value || "").trim();
      }
      if (rate === null || rate === 21) {
        const fallback = items.find((item) => item?.code === "ERP_CONSUM_INVOICE_SERVICE_ARTICLE_CODE");
        return String(fallback?.value || "").trim();
      }
      return "";
    },
    invoiceServiceDescriptionForVat(vatRate = null) {
      const rate = this.normalizeVatRate(vatRate);
      if (rate !== null) {
        return `Prestari servicii agricole conform PV anexate (${rate}%)`;
      }
      return this.$t("tools.erp_integration.consum_invoice_service_description");
    },
    normalizeVatRate(value) {
      if (value === null || value === undefined || value === "" || typeof value === "boolean") {
        return null;
      }
      const cleaned = typeof value === "string" ? value.replace("%", "").trim() : value;
      if (cleaned === "" || !Number.isFinite(Number(cleaned))) {
        return null;
      }
      const rate = Math.round(Number(cleaned));
      return rate >= 0 ? rate : null;
    },
    formatVatRate(value) {
      const rate = this.normalizeVatRate(value);
      return rate === null
        ? this.$t("tools.erp_integration.consum_erp_summary_price_tbd")
        : `${rate}%`;
    },
    workItemKey(row) {
      if (row.has_consum === false) {
        return row.erp_invoice_work_key || `invoice-work:${row.work_sheet_uuid}`;
      }
      return row.erp_invoice_work_key || `invoice-work:${JSON.stringify([
        row.work_uuid || row.work_sheet_uuid, row.parcel_uuid || row.parcel_name,
        row.season_uuid, this.invoiceWorkScope(row),
      ])}`;
    },
    invoiceWorkScope(row) {
      if (row.erp_invoice_work_scope) {
        return row.erp_invoice_work_scope;
      }
      const seller = row.has_consum === false ? row.department_uuid
        : row.warehouse_department_uuid || row.source_department_uuid;
      const period = String(row.consum_date || row.work_date || "").slice(0, 7);
      return JSON.stringify([seller, row.parcel_department_uuid, period]);
    },
    exportSelection() {
      const rows = this.selectedRows || [];
      const workUuids = rows.filter((row) => row.has_consum === false).map((row) => row.work_sheet_uuid);
      return {
        consum_uuids: rows.filter((row) => row.has_consum !== false).map((row) => row.uuid).filter(Boolean),
        ...(workUuids.length ? { work_sheet_uuids: workUuids } : {}),
      };
    },
    formatQuantity(value) {
      return Math.round((Number(value) || 0) * 10000) / 10000;
    },
    normalizeInvoicePrice(value) {
      if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
        return null;
      }
      const price = Number(value);
      return price >= 0 ? price : null;
    },
    formatInvoicePrice(value) {
      const price = this.normalizeInvoicePrice(value);
      return price === null
        ? this.$t("tools.erp_integration.consum_erp_summary_price_tbd")
        : this.formatQuantity(price);
    },
  },
};
</script>

<style scoped>
::v-deep .dx-popup-content {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.consum-summary-dialog-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  flex: 1;
}

/* Letisztult, természetes tab fül stílus, ami nem töri meg a kattinthatóságot */
.erp-summary-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.erp-summary-tabs ::v-deep .dx-tabpanel-tabs {
  margin-bottom: 12px;
  border-bottom: 1px solid #dfe6ef;
}

.erp-summary-tabs ::v-deep .dx-tab {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
  padding: 10px 18px;
  transition: color 0.15s ease;
}

.erp-summary-tabs ::v-deep .dx-tab:hover {
  color: #1f7a3f;
}

.erp-summary-tabs ::v-deep .dx-tab.dx-tab-selected {
  color: #1f7a3f;
  font-weight: 700;
}

.erp-summary-tabs ::v-deep .dx-tab.dx-tab-selected .dx-tab-text {
  color: #1f7a3f;
}

.erp-summary-tabs ::v-deep .dx-tabs-indicator {
  background-color: #1f7a3f;
  height: 3px;
}

::v-deep .erp-summary-tabs .dx-tabpanel-container {
  flex: 1;
  min-height: 0;
}

::v-deep .erp-summary-tabs .dx-multiview-wrapper {
  height: 100%;
}

::v-deep .erp-summary-tabs .dx-multiview-item-content {
  overflow-y: auto;
  height: 100%;
  padding: 4px 2px 10px 2px;
}

.preview-section {
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  padding: 12px;
  background: #ffffff;
}

.export-items-section {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.export-items-grid {
  flex: 1;
  min-height: 0;
}

.preview-ok {
  background: #f2f9f4;
  border: 1px solid #cfe8d8;
  color: #1e7e34;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
}

.preview-table-wrap {
  overflow-x: auto;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.preview-table th,
.preview-table td {
  border: 1px solid #dfe6ef;
  padding: 8px 10px;
  text-align: left;
  vertical-align: middle;
}

.preview-table th {
  background: #e8f4ec;
  font-weight: 700;
  color: #163b22;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.3px;
}

.product-item {
  line-height: 1.5;
}

/* Kimenő számlák és részleteik kártyakonténerei */
.outgoing-invoices-container,
.incoming-invoices-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Kimenő számlák részleteinek al-fülei */
.incoming-subtabs-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dfe6ef;
}

.subtab-btn {
  background: #f4f7fb;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #3b506c;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease-in-out;
}

.subtab-btn:hover {
  background: #e9f0f9;
  border-color: #9ab8de;
  color: #183052;
}

.subtab-btn--active {
  background: #1f7a3f;
  border-color: #1f7a3f;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(31, 122, 63, 0.25);
}

.subtab-badge {
  background: rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 700;
}

.subtab-btn--active .subtab-badge {
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

.subtab-title {
  font-weight: 700;
}

.subtab-count {
  font-size: 11px;
  opacity: 0.85;
}

.outgoing-invoice-card,
.incoming-invoice-card {
  border: 2px solid #1f7a3f;
  border-left: 8px solid #1f7a3f;
  border-radius: 10px;
  background: #fdfefe;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(31, 122, 63, 0.08);
}

.invoice-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  border-bottom: 2px solid #e8f4ec;
  padding-bottom: 8px;
}

.invoice-title-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.invoice-badge {
  background: #1f7a3f;
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.invoice-badge--incoming {
  background: #155724;
}

.invoice-owner-name {
  margin: 0;
  font-weight: 800;
  font-size: 16px;
  color: #163b22;
}

.invoice-meta-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #e8f4ec;
  border: 1px solid #b8d6c2;
  border-radius: 14px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  color: #163b22;
}

.meta-pill--reverse-charge {
  background: #fff3cd;
  border-color: #ffecb5;
  color: #664d03;
}

.invoice-card-meta {
  background: #f4f9f7;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}

.meta-row {
  display: flex;
  gap: 6px;
}

.meta-label {
  font-weight: 600;
  color: #4b6354;
  min-width: 170px;
}

.meta-value {
  color: #163b22;
  font-weight: 700;
}

.invoice-lines-table th {
  background: #edf5f0;
}

.cost-center-tag {
  display: inline-block;
  font-weight: 700;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
}

.cost-center-tag--landserv {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.cost-center-tag--inputserv {
  background: #cce5ff;
  color: #004085;
  border: 1px solid #b8daff;
}

.cost-center-tag--incoming {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

/* Kimenő számlák alrészletei */
.invoice-subsection {
  margin-top: 14px;
  background: #ffffff;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  padding: 10px 12px;
}

.subsection-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.subsection-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #1f5a2f;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.preview-note {
  margin-top: 10px;
  font-size: 12px;
  color: #5d7568;
  font-style: italic;
}

/* Gombok és lábléc */
.settings-actions {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #dfe6ef;
}

.erp-send-button {
  background: #1f7a3f;
  border: 1px solid #1f7a3f;
  border-radius: 4px;
  color: #ffffff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  padding: 8px 18px;
  transition: all 0.2s ease;
}

.erp-send-button:hover:not(:disabled) {
  background: #165e30;
  border-color: #165e30;
}

.erp-send-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Reszponzív nézetek mobilra és tabletre */
@media (max-width: 768px) {
  .invoice-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .invoice-meta-badges {
    width: 100%;
    justify-content: flex-start;
  }

  .meta-row {
    flex-direction: column;
    gap: 2px;
  }

  .meta-label {
    min-width: unset;
  }
}

.mapping-alert-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-left: 4px solid #d97706;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
  gap: 12px;
}

.mapping-alert-content {
  display: flex;
  align-items: center;
  color: #92400e;
  font-size: 13px;
  font-weight: 500;
}

.mapping-alert-content i {
  color: #d97706;
  font-size: 16px;
}

.mapping-alert-btn {
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  border-color: #d97706;
  color: #92400e;
  background: #ffffff;
}

.mapping-alert-btn:hover {
  background: #fef3c7;
  color: #78350f;
}
</style>
